import { Link } from '@material-ui/core';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import EventIcon from '@material-ui/icons/Event';
import GroupIcon from '@material-ui/icons/Group';
import HearingIcon from '@material-ui/icons/Hearing';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import { CochlearSelectedTabKey } from 'app/localStorageKeys';
import { PageBreadcrumbDivider } from 'framework/components/breadcrumbs/PageBreadcrumbDivider';
import { PageBreadcrumbTitle } from 'framework/components/breadcrumbs/PageBreadcrumbTitle';
import { PageReloadButtonX } from 'framework/components/layout/PageReloadButtonX';
import { PageXHeaderActions } from 'framework/components/layout/PageXHeaderActions';
import { TabsX } from 'framework/components/tabs/TabsX';
import { TabX } from 'framework/components/tabs/TabX';
import { useLocalStorage } from 'framework/hooks/useLocalStorage';
import { useTrigger } from 'framework/hooks/useTrigger';
import { createType } from 'framework/utils/createType';
import Fg1 from 'libs/ui/layout/fg1';
import RowStack from 'libs/ui/layout/stack/row.stack';
import { useLocalization } from 'localization/useLocalization';
import React, { useEffect } from 'react';
import { RestrictedPageX } from 'shared/layout/RestrictedPageX';
import { IntegrationsPageBreadcrumb } from '../IntegrationsPageBreadcrumb';
import { CochlearBasicInfoTab } from './cochlear-basic-info.tab';
import { CochlearCounselingGuideTab } from './cochlear-counseling-guide.tab';
import { CochlearEventsTab } from './cochlear-events.tab';
import { CochlearPartnerTrainingTab } from './cochlear-partner-training.tab';
import { CochlearPatientsTab } from './cochlear-patients.tab';
import { CochlearReferralCentersTab } from './cochlear-referral-centers.tab';
import { RegisterAsCochlearSpecialistButton } from './cochlear.components';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { getCochlearSettings_handle, ICochlearSettingsDto } from 'gen/ApiClient';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { CochlearSettingsTab } from './cochlear-settings.tab';
import SettingsIcon from '@material-ui/icons/Settings';

export const CochlearTabTypes = createType(
	'patients',
	'events',
	'referral_centers',
	'basic_info',
	'partner_training_modules',
	'counseling_guide_modules',
	'settings'
);
export type CochlearTabType = typeof CochlearTabTypes[number];

export const CochlearPage = () => {
	const [reloadTrigger, reload] = useTrigger();
	const [selectedTab, setSelectedTab] = useLocalStorage<CochlearTabType>(CochlearSelectedTabKey, 'patients');
	const [settings, reloadSettings] = useApiEffect(getCochlearSettings_handle);

	useLazyEffect(() => {
		reloadSettings();
	}, [reloadTrigger]);

	useEffect(() => {
		if (settings !== undefined && settings.enabled === false) {
			setSelectedTab('settings');
		}
		// eslint-disable-next-line
	}, [settings]);

	if (settings === undefined) {
		return <></>;
	}

	return (
		<RestrictedPageX
			pageClaim='CochlearIntegration'
			stickyHeader={
				<CochlearPageHeader
					tab={selectedTab}
					setTab={setSelectedTab}
					reload={reload}
					settings={settings}
				/>
			}>
			{selectedTab === 'events' && (
				<CochlearEventsTab
					reload={reload}
					reloadTrigger={reloadTrigger}
					settings={settings}
				/>
			)}
			{selectedTab === 'referral_centers' && (
				<CochlearReferralCentersTab
					reload={reload}
					reloadTrigger={reloadTrigger}
				/>
			)}
			{selectedTab === 'partner_training_modules' && (
				<>
					{settings.isPartner === false && (
						<div
							className='df-col gap-8 w100'
							style={{ padding: '16px' }}>
							<div>
								<RegisterAsCochlearSpecialistButton settings={settings} />
							</div>
						</div>
					)}
					{settings.isPartner === true && (
						<CochlearPartnerTrainingTab
							reload={reload}
							reloadTrigger={reloadTrigger}
						/>
					)}
				</>
			)}
			{selectedTab === 'counseling_guide_modules' && (
				<>
					{settings.isPartner === false && (
						<div
							className='df-col gap-8 w100'
							style={{ padding: '16px' }}>
							<div>
								<RegisterAsCochlearSpecialistButton settings={settings} />
							</div>
						</div>
					)}
					{settings.isPartner === true && (
						<CochlearCounselingGuideTab
							reload={reload}
							reloadTrigger={reloadTrigger}
						/>
					)}
				</>
			)}
			{selectedTab === 'basic_info' && <CochlearBasicInfoTab settings={settings} />}
			{selectedTab === 'patients' && (
				<CochlearPatientsTab
					reload={reload}
					reloadTrigger={reloadTrigger}
					settings={settings}
				/>
			)}
			{selectedTab === 'settings' && (
				<CochlearSettingsTab
					reload={reload}
					settings={settings}
				/>
			)}
		</RestrictedPageX>
	);
};

type CochlearPageHeaderProps = {
	reload: VoidFunction;
	tab: CochlearTabType;
	setTab: React.Dispatch<React.SetStateAction<CochlearTabType>>;
	settings: ICochlearSettingsDto;
};

const CochlearPageHeader = ({ reload, tab, setTab, settings }: CochlearPageHeaderProps) => {
	const strings = useLocalization();

	return (
		<div className='df-col fg1 gap-4'>
			<div className='df-row-ac'>
				<IntegrationsPageBreadcrumb />
				<PageBreadcrumbDivider />
				<PageBreadcrumbTitle title={`Cochlear`} />
				<div className='fg1'></div>
				<img
					src={'https://hamspublicfiles.blob.core.windows.net/cochlear/cochlear_logo.png'}
					alt='Cochlear logo'
					style={{ height: '32px', marginRight: '8px' }}
				/>
				<PageXHeaderActions>
					<PageReloadButtonX reload={reload} />
				</PageXHeaderActions>
			</div>
			<RowStack
				dfrowac
				gap={0.5}>
				<Fg1 />
				<PhoneIcon style={{ fontSize: '16px' }} />
				<Link href={`tel:003215795577`}>{`+32 (0)1579 55 77`}</Link>
				<MailOutlineIcon style={{ fontSize: '16px', marginLeft: '16px' }} />
				<Link href={`mailto:marketing.bnl@cochlear.com`}>{`Marketing.bnl@cochlear.com`}</Link>
			</RowStack>
			<TabsX
				style={{
					marginLeft: -4,
				}}>
				{settings.enabled === true && (
					<>
						<TabX
							isSelected={tab === 'patients'}
							onClick={() => setTab('patients')}
							value={strings.patients}
							startIcon={<GroupIcon />}
						/>
						<TabX
							isSelected={tab === 'events'}
							onClick={() => setTab('events')}
							value={strings.events}
							startIcon={<EventIcon />}
						/>
						<TabX
							isSelected={tab === 'referral_centers'}
							onClick={() => setTab('referral_centers')}
							value={strings.ciCentra}
							startIcon={<DoubleArrowIcon />}
						/>
						<TabX
							isSelected={tab === 'basic_info'}
							onClick={() => setTab('basic_info')}
							value={strings.basicInfo}
							startIcon={<InfoOutlinedIcon />}
						/>
						<TabX
							isSelected={tab === 'partner_training_modules'}
							onClick={() => setTab('partner_training_modules')}
							value={strings.partnerInfo}
							startIcon={<SettingsOutlinedIcon />}
						/>
						<TabX
							isSelected={tab === 'counseling_guide_modules'}
							onClick={() => setTab('counseling_guide_modules')}
							value={strings.counselingGuide}
							startIcon={<HearingIcon />}
						/>
					</>
				)}
				<TabX
					isSelected={tab === 'settings'}
					onClick={() => setTab('settings')}
					value={strings.settings}
					startIcon={<SettingsIcon />}
				/>
				<Fg1 />
			</TabsX>
		</div>
	);
};
